import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { twofaEnroll, twofaVerify, type EnrollResponse } from "../api/auth";

export function TwoFactorEnrollPage() {
  const { api } = useAuth();
  const nav = useNavigate();
  const [enrollment, setEnrollment] = useState<EnrollResponse | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function startEnroll() {
    setEnrollBusy(true);
    setError(null);
    try {
      setEnrollment(await twofaEnroll(api));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "enroll failed");
    } finally {
      setEnrollBusy(false);
    }
  }

  useEffect(() => {
    if (!enrollment) startEnroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await twofaVerify(api, code);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "verify failed");
    } finally {
      setBusy(false);
    }
  }

  if (enrollBusy && !enrollment) {
    return <p className="meta">Generating TOTP secret…</p>;
  }

  if (done) {
    return (
      <div className="card">
        <h1>Two-factor enabled</h1>
        <p className="meta">Keep the recovery codes shown above — each works once if you lose your authenticator.</p>
        <button className="btn primary" onClick={() => nav("/")}>Back to account</button>
      </div>
    );
  }

  if (!enrollment) return <div className="error">{error ?? "Failed to load enrollment."}</div>;

  return (
    <div className="vstack">
      <div className="card">
        <h1>Enable two-factor</h1>
        <p className="meta">Scan with an authenticator (1Password, Raivo, Authy, Google Authenticator) or enter the secret manually.</p>
        <div className="qr-wrap">
          <QRCodeSVG
            value={enrollment.provisioning_uri}
            size={196}
            level="M"
            bgColor="#ffffff"
            fgColor="#000000"
            marginSize={2}
          />
        </div>
        <h2>Manual-entry secret</h2>
        <div className="code">{enrollment.secret_base32}</div>
      </div>
      <div className="card">
        <h1>Recovery codes</h1>
        <p className="meta">Store these somewhere safe. Each code works exactly once.</p>
        <div className="recovery-list">
          {enrollment.recovery_codes.map((c) => <code key={c}>{c}</code>)}
        </div>
      </div>
      <form className="card" onSubmit={verify}>
        <h1>Confirm a code</h1>
        <p className="meta">Enter the 6-digit code your authenticator shows now.</p>
        <div className="field">
          <label htmlFor="code">Code</label>
          <input id="code" type="text" inputMode="numeric" autoComplete="one-time-code"
            value={code} required onChange={(e) => setCode(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy || code.length < 6}>
          {busy ? "Confirming…" : "Confirm"}
        </button>
      </form>
    </div>
  );
}
